import lang from 'lang'
import settingsConfig from '@/modules/settings/config'
import request from '@/modules/base/util/request'
import { batch } from 'react-redux'

import { toast } from '@/modules/base/components/toast/toastConfig'
import { errorCodes } from '@/modules/base/util/constants'
import { uploadFile } from '@/modules/_helpers/uploadFile'

const ACTION_PREFIX = 'settings/letterPaper'
const SET_LOADING = `${ACTION_PREFIX}/SET_LOADING`
const SET_ITEMS = `${ACTION_PREFIX}/SET_ITEMS`
const UPSERT_ITEM = `${ACTION_PREFIX}/UPDATE_ITEM`
const REMOVE_ITEM = `${ACTION_PREFIX}/REMOVE_ITEM`
const SELECT_ITEM = `${ACTION_PREFIX}/SELECT_ITEM`
const SET_AS_DEFAULT = `${ACTION_PREFIX}/SET_AS_DEFAULT`
const OPEN_DRAWER = `${ACTION_PREFIX}/OPEN_DRAWER`
const CLOSE_DRAWER = `${ACTION_PREFIX}/CLOSE_DRAWER`
const ADD_NEW = `${ACTION_PREFIX}/ADD_NEW`

const getEmptyItem = () => ({
  _isNew: true,
  name: '',
  isDefault: false,
  url: null,
})

const initialState = {
  isLoading: false,
  openDrawer: false,
  selectedItem: null,
  defaultId: null,
  items: [],
}

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case SET_LOADING:
      return { ...state, isLoading: action.isLoading }

    case SET_ITEMS:
      return { ...state, items: action.items }

    case UPSERT_ITEM: {
      const { item: newItem } = action
      let newItems = null
      if (state.items.find(item => item.id === newItem.id)) {
        newItems = state.items.map(item => item.id === newItem.id ? newItem : item)
      } else {
        newItems = [...state.items, newItem]
      }

      return { ...state, items: newItems }
    }

    case SELECT_ITEM:
      return { ...state, selectedItem: state.items.find(item => item.id === action.id) }

    case REMOVE_ITEM:
      return { ...state, items: state.items.filter(item => item.id !== action.id) }

    case ADD_NEW:
      return { ...state, selectedItem: getEmptyItem(), openDrawer: true }

    case SET_AS_DEFAULT:
      return { ...state, defaultId: action.id }

    case OPEN_DRAWER:
      return { ...state, openDrawer: true }

    case CLOSE_DRAWER:
      return { ...state, openDrawer: false }

    default:
      return state
  }
}

// action creators

const setLoading = (isLoading) => {
  return {
    type: SET_LOADING,
    isLoading,
  }
}

const setAsDefault = (id) => {
  return {
    type: SET_AS_DEFAULT,
    id,
  }
}

const removeItem = (id) => {
  return {
    type: REMOVE_ITEM,
    id,
  }
}

export const openDrawer = () => {
  return {
    type: OPEN_DRAWER,
  }
}

export const closeDrawer = () => {
  return {
    type: CLOSE_DRAWER,
  }
}

export const selectItem = (id) => {
  return {
    type: SELECT_ITEM,
    id,
  }
}

export const addNew = (id) => {
  return {
    type: ADD_NEW,
    id,
  }
}

export const setItems = (data) => {
  return {
    type: SET_ITEMS,
    items: data,
  }
}

export const upsertItem = (data) => {
  return {
    type: UPSERT_ITEM,
    item: data,
  }
}

export const fetchData = () => {
  return (dispatch, getState) => {
    const { isLoading } = getState().settings.letterPaper
    if (!isLoading) {
      dispatch(setLoading(true))
      return request(settingsConfig.letterPaperPages, { auth: true, isConcurrent: true })
        .then(({ body: { data, meta: { currentLetterPaperPageId } } }) => {
          batch(() => {
            dispatch(setItems(data))
            dispatch(setAsDefault(currentLetterPaperPageId))
            dispatch(selectItem(currentLetterPaperPageId))
          })
        })
        .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
        .finally(() => dispatch(setLoading(false)))
    }
  }
}

export const submitSetNewDefault = (id) => {
  const postData = { isDefault: true }
  return (dispatch) => {
    return request(`${settingsConfig.letterPaperPages}/${id}`, { method: 'PUT', auth: true, data: postData })
      .then(({ body: { data } }) => {
        toast.showToast({ message: lang.settings.letterPaperSettings.updateSuccessMessage })
        dispatch(setAsDefault(data.id))
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }
}

export const deleteItem = (id) => {
  return (dispatch) => {
    return request(`${settingsConfig.letterPaperPages}/${id}`, { method: 'DELETE', auth: true })
      .then(({ body: { data: { name }, meta: { currentLetterPaperPageId } } }) => {
        toast.showToast({ message: lang.settings.letterPaperSettings.deleteSuccessMessage(name) })

        batch(() => {
          dispatch(removeItem(id))
          dispatch(setAsDefault(currentLetterPaperPageId))
        })
        dispatch(selectItem(currentLetterPaperPageId))
      })
      .catch(() => toast.showToast({ type: 'error', message: lang.defaultErrorMessage }))
  }
}

export const submitAddOrUpdate = (formData) => {
  return (dispatch) => {
    const isNew = formData._isNew
    let response = null

    if (isNew) {
      const postData = { name: formData.name }
      if (formData.isDefault) {
        postData.isDefault = 'true'
      }

      if (formData.file) {
        response = uploadFile(settingsConfig.letterPaperPages, formData.file, postData)
      } else {
        const multipartData = new FormData()
        multipartData.append('name', postData.name)
        if (formData.isDefault) {
          multipartData.append('isDefault', 'true')
        }
        response = request(settingsConfig.letterPaperPages, {
          method: 'POST',
          auth: true,
          data: multipartData,
          headers: { 'Content-Type': 'multipart/form-data' },
        })
      }
    } else {
      response = request(`${settingsConfig.letterPaperPages}/${formData.id}`, { method: 'PUT', auth: true, data: formData })
    }

    return response.then(({ body: { data, meta: { currentLetterPaperPageId } } }) => {
      const message = isNew ? lang.settings.letterPaperSettings.addSuccessMessage : lang.settings.letterPaperSettings.updateSuccessMessage
      toast.showToast({ message })

      batch(() => {
        dispatch(upsertItem(data))
        dispatch(setAsDefault(currentLetterPaperPageId))
        dispatch(selectItem(data.id))
      })
    }).catch((e) => {
      if (e.body?.meta?.size[0]?.code === errorCodes.TOO_LARGE) {
        toast.showToast({ type: 'error', message: lang.fileSizeLimitErrorMessage(1) })
      } else {
        toast.showToast({ type: 'error', message: lang.defaultErrorMessage })
      }
      throw e
    })
  }
}
